import { ROUTES } from '@eliadmin/core/src/constants/routes';
import type { AvailableLocales } from '@eliadmin/core/src/stores/locale-store';
import type { CookieConsentConfig } from 'vanilla-cookieconsent';

type CookieConsentLocale = AvailableLocales;

type CookieConsentTranslationKey =
  | 'text272'
  | 'text994'
  | 'text995'
  | 'text996'
  | 'text997'
  | 'text998'
  | 'text999'
  | 'text1000'
  | 'text1001'
  | 'text1002'
  | 'text1003'
  | 'text1004'
  | 'text1005'
  | 'text1006'
  | 'text1007'
  | 'text1008'
  | 'text1009'
  | 'text1010'
  | 'text1011'
  | 'text1012'
  | 'text1013'
  | 'text1014'
  | 'text1015'
  | 'text1016'
  | 'text1017';

type CookieConsentTranslationMap = Record<
  CookieConsentTranslationKey,
  Partial<Record<CookieConsentLocale, string>>
>;

export const COOKIE_CONSENT_TRANSLATIONS: CookieConsentTranslationMap = {
text272: {en: 'Close',de: 'Schließen'},
text994: {en: 'This website uses cookies',de: 'Diese Website verwendet Cookies'},
text995: {en: 'We use cookies to personalise content and ads, provide social media features and analyse traffic. We also provide information about how you use our website to our social media, advertising and analytics partners. These partners may combine the relevant information with other information that you have provided to them or that they have collected from you when you have used their services.',de: 'Wir verwenden Cookies, um Inhalte und Anzeigen zu personalisieren, Funktionen für soziale Medien bereitzustellen und den Datenverkehr zu analysieren. Wir stellen auch Informationen darüber, wie Sie unsere Website nutzen, unseren Partnern für soziale Medien, Werbung und Analysen zur Verfügung. Diese Partner können die relevanten Informationen mit anderen Informationen kombinieren, die Sie ihnen zur Verfügung gestellt haben oder die sie von Ihnen gesammelt haben, als Sie ihre Dienste genutzt haben.'},
text996: {en: 'Enable everything',de: 'Alles aktivieren'},
text997: {en: 'Reject',de: 'Ablehnen'},
text998: {en: 'Customize',de: 'Anpassen'},
text999: {en: 'Cookies settings',de: 'Cookies setzen'},
text1000: {en: 'Services',de: 'Dienstleistungen'},
text1001: {en: 'Your privacy options',de: 'Ihre Datenschutzoptionen'},
text1002: {en: 'In this section you can express some preferences regarding the processing of your personal data. You can review and change the expressed preferences at any time by revisiting this panel via the link provided. If you wish to refuse your consent to the specific processing activities described below, toggle the toggle switch to off or use the "Refuse All" button to confirm that you wish to save your choices.',de: 'In diesem Abschnitt können Sie einige Präferenzen bezüglich der Verarbeitung Ihrer personenbezogenen Daten äußern. Sie können die getroffenen Entscheidungen jederzeit überprüfen und ändern, indem Sie dieses Panel über den bereitgestellten Link erneut aufrufen. Wenn Sie Ihre Zustimmung zu den unten beschriebenen spezifischen Verarbeitungsaktivitäten verweigern möchten, schalten Sie die Schalter auf „Aus“ oder verwenden Sie die Schaltfläche „Alle ablehnen“ und bestätigen Sie, dass Sie Ihre Auswahl speichern möchten.'},
text1003: {en: 'Needed',de: 'Notwendige Cookies helfen bei der Erstellung nutzbarer Websites, indem sie grundlegende Funktionen wie die Seitennavigation und den Zugriff auf geschützte Bereiche von Websites ermöglichen. Ohne diese Cookies können Websites nicht ordnungsgemäß funktionieren.'},
text1004: {en: 'Necessary cookies help to create usable websites by enabling basic functions such as site navigation and access to protected areas of the website. The website cannot function properly without these cookies.',de: 'Notwendige Cookies helfen bei der Erstellung nutzbarer Websites, indem sie grundlegende Funktionen wie die Seitennavigation und den Zugriff auf geschützte Bereiche von Websites ermöglichen. Ohne diese Cookies können Websites nicht ordnungsgemäß funktionieren.'},
text1005: {en: 'Statistics and analysis',de: 'Statistik und Analyse'},
text1006: {en: 'Statistical cookies help website owners understand how to interact with website visitors by collecting and reporting information anonymously.',de: 'Statistische Cookies helfen Website-Besitzern zu verstehen, wie sie mit Website-Besuchern interagieren, indem sie Informationen anonym sammeln und melden.'},
text1007: {en: 'Cookie table',de: 'Keks-Tabelle'},
text1008: {en: 'Cookie',de: 'Keks'},
text1009: {en: 'Domain',de: 'Bereich'},
text1010: {en: 'Marketing',de: 'Marketing'},
text1011: {en: 'Marketing cookies are used to track visitors to the website. The intent is to serve ads that are relevant and engaging to individual users and thus more valuable to publishers and third-party advertisers.',de: 'Marketing-Cookies werden verwendet, um Besucher auf Websites zu verfolgen. Ziel ist es, Anzeigen anzuzeigen, die für einzelne Nutzer relevant und ansprechend sind und somit für Publisher und Drittwerbetreibende wertvoller sind.'},
text1012: {en: 'I want to know more',de: 'Ich möchte mehr wissen'},
text1013: {en: 'For information about the processing of personal data and your rights in relation to the protection of personal data, you can contact us',de: 'Um Informationen über die Verarbeitung personenbezogener Daten und Ihre Rechte im Zusammenhang mit dem Schutz personenbezogener Daten zu erhalten, können Sie sich an uns wenden'},
text1014: {en: 'here.',de: 'hier.'},
text1015: {en: 'Functional',de: 'Funktional'},
text1016: {en: 'Functional cookies help perform certain functions, such as sharing website content on social media platforms, collecting feedback and other third-party features.',de: 'Funktionalitätscookies helfen bei der Ausführung bestimmter Funktionen, wie z. B. dem Teilen von Website-Inhalten auf Social-Media-Plattformen, dem Sammeln von Feedback und anderen Funktionen von Drittanbietern.'},
text1017: {en: 'Save settings',de: 'Einstellungen speichern'}
};

function getCookieConsentText(locale: AvailableLocales, key: CookieConsentTranslationKey) {
  const translations = COOKIE_CONSENT_TRANSLATIONS[key];
  return translations[locale as CookieConsentLocale] || translations.en || key;
}

function createCookieConsentTranslator(locale: AvailableLocales) {
  return (key: CookieConsentTranslationKey) => getCookieConsentText(locale, key);
}

export const getCookieConsentConfig = (locale: AvailableLocales) => {
  const ct = createCookieConsentTranslator(locale);

  const config: CookieConsentConfig = {
    disablePageInteraction: false,
    autoShow: true,
    hideFromBots: true,
    mode: 'opt-in',

    guiOptions: {
      consentModal: {
        layout: 'box inline',
        position: 'bottom left',
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {},
      ads: {},
      functional: {},
    },

    language: {
      default: locale,
      translations: {
        [locale]: {
          consentModal: {
            title: ct('text994'),
            description: getConsentModalSummary(ct('text995')),
            acceptAllBtn: ct('text996'),
            acceptNecessaryBtn: ct('text997'),
            showPreferencesBtn: ct('text998'),
          },
          preferencesModal: {
            title: ct('text999'),
            acceptAllBtn: ct('text996'),
            acceptNecessaryBtn: ct('text997'),
            savePreferencesBtn: ct('text1017'),
            closeIconLabel: ct('text272'),
            serviceCounterLabel: ct('text1000'),
            sections: [
              {
                title: ct('text1001'),
                description: ct('text1002'),
              },
              {
                title: ct('text1003'),
                description: ct('text1004'),
                linkedCategory: 'necessary',
              },
              {
                title: ct('text1015'),
                description: ct('text1016'),
                linkedCategory: 'functional',
              },
              {
                title: ct('text1005'),
                description: ct('text1006'),
                linkedCategory: 'analytics',
              },
              {
                title: ct('text1010'),
                description: ct('text1011'),
                linkedCategory: 'ads',
              },
              {
                title: ct('text1012'),
                description: `${ct('text1013')} <a href="${ROUTES.CONTACT()}" target="_blank">${ct(
                  'text1014',
                )}</a>`,
              },
            ],
          },
        },
      },
    },
  };

  return config;
};

function getConsentModalSummary(description: string) {
  const firstSentence = description.match(/^.*?[.!?](?:\s|$)/u)?.[0]?.trim();
  return firstSentence || description;
}
